import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Coins, Loader2, Sun, Check } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const COLORS = { starter: "bg-white", popular: "bg-[#FFC72C]", mega: "bg-[#FFF0F3]" };

const untilMidnight = () => {
  const n = new Date();
  const m = new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate() + 1));
  const s = Math.floor((m - n) / 1000);
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};

export default function Credits() {
  const { user } = useAuth();
  const [packs, setPacks] = useState([]);
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.get("/credit-packs").then((r) => setPacks(r.data)); }, []);

  const buy = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post("/payments/checkout", { package_id: id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatError(err));
      setBusy(null);
    }
  };

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="credits-page">
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Credits</h1>
      <div className="mt-8 grid md:grid-cols-2 gap-6">
        <div className="rounded-3xl bg-white border border-[#EAE3D9] p-6" data-testid="daily-credits-card">
          <div className="flex items-center gap-2 text-sm font-bold text-[#6E687A]"><Sun className="h-4 w-4 text-[#FFC72C]" /> Daily free credits</div>
          <p className="mt-2 font-display text-4xl font-bold">{user?.free_credits} <span className="text-lg text-[#6E687A]">/ 5</span></p>
          <div className="mt-3 h-3 rounded-full bg-[#F5F0E8] overflow-hidden"><div className="h-full bg-[#36D399] transition-[width] duration-500" style={{ width: `${(user?.free_credits / 5) * 100}%` }} /></div>
          <p className="mt-2 text-xs text-[#6E687A]">Refills in {untilMidnight()} (UTC midnight)</p>
        </div>
        <div className="rounded-3xl bg-[#1E1B26] text-white p-6" data-testid="paid-credits-card">
          <div className="flex items-center gap-2 text-sm font-bold text-white/70"><Coins className="h-4 w-4 text-[#FFC72C]" /> Bonus credits</div>
          <p className="mt-2 font-display text-4xl font-bold">{user?.paid_credits}</p>
          <p className="mt-3 text-sm text-white/70">Never expire. Used after your daily credits run out.</p>
        </div>
      </div>

      <h2 className="mt-14 text-2xl sm:text-3xl font-bold">Top up your treat jar</h2>
      <div className="mt-6 grid md:grid-cols-3 gap-6">
        {packs.map((p) => (
          <div key={p.id} className={`relative rounded-3xl p-7 ${COLORS[p.id]} ${p.id === "popular" ? "sticker md:-translate-y-3" : "border border-[#EAE3D9]"}`} data-testid={`credit-pack-${p.id}`}>
            {p.id === "popular" && <span className="absolute -top-3 left-6 rounded-full bg-[#FF5A79] text-white px-3 py-1 text-xs font-bold uppercase tracking-wider">Most loved</span>}
            <p className="font-display text-xl font-semibold">{p.name}</p>
            <p className="mt-3 font-display text-5xl font-bold">{p.credits}<span className="text-base font-semibold"> credits</span></p>
            <p className="mt-1 font-mono text-sm">${p.price.toFixed(2)} · ${(p.price / p.credits).toFixed(2)}/creation</p>
            <ul className="mt-5 space-y-2 text-sm">
              {["All viral styles", "HD downloads", "Never expires"].map((f) => <li key={f} className="flex items-center gap-2"><Check className="h-4 w-4 text-[#36D399]" />{f}</li>)}
            </ul>
            <button onClick={() => buy(p.id)} disabled={!!busy} data-testid={`buy-credits-pack-${p.id}`}
              className="mt-6 w-full flex items-center justify-center gap-2 rounded-full bg-[#1E1B26] text-white py-3 font-bold hover:bg-[#FF5A79] transition-colors disabled:opacity-60">
              {busy === p.id && <Loader2 className="h-4 w-4 animate-spin" />} Buy now
            </button>
          </div>
        ))}
      </div>
    </main>
  );
}
